import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'

interface StarRatingDisplayProps {
  rating: number
  count?: number
  size?: 'sm' | 'md' | 'lg'
  className?: string
  /** Hide the numeric score (stars only). */
  starsOnly?: boolean
}

export function StarRatingDisplay({
  rating,
  count,
  size = 'sm',
  className,
  starsOnly = false,
}: StarRatingDisplayProps) {
  if (!Number.isFinite(rating) || rating <= 0) return null

  const starClass =
    size === 'lg' ? 'h-5 w-5' : size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'
  const starBoxClass = starClass
  const rounded = Math.round(rating * 2) / 2

  return (
    <div
      className={cn('inline-flex items-center gap-1.5', className)}
      aria-label={`Rated ${rating} out of 5${count ? ` from ${count} reviews` : ''}`}
    >
      <div className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => {
          const filled = rounded >= i + 1
          const half = !filled && rounded >= i + 0.5
          const fillWidth = filled ? '100%' : half ? '50%' : '0%'

          return (
            <span
              key={i}
              className={cn('relative inline-flex shrink-0', starBoxClass)}
              aria-hidden
            >
              <Star className={cn(starClass, 'fill-transparent text-zinc-300')} />
              {fillWidth !== '0%' && (
                <span
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: fillWidth }}
                >
                  <Star className={cn(starClass, 'fill-gold-500 text-gold-500')} />
                </span>
              )}
            </span>
          )
        })}
      </div>
      {!starsOnly && (
        <span
          className={cn(
            'font-medium text-zinc-600',
            size === 'lg' ? 'text-base' : size === 'md' ? 'text-sm' : 'text-xs'
          )}
        >
          {rating.toFixed(1)}
          {count != null && count > 0 ? (
            <span className="font-normal text-zinc-400"> ({count})</span>
          ) : null}
        </span>
      )}
    </div>
  )
}

interface StarRatingInputProps {
  value: number
  onChange: (rating: number) => void
  disabled?: boolean
  className?: string
}

export function StarRatingInput({
  value,
  onChange,
  disabled = false,
  className,
}: StarRatingInputProps) {
  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      role="radiogroup"
      aria-label="Rating"
    >
      {Array.from({ length: 5 }).map((_, i) => {
        const starValue = i + 1
        const active = starValue <= value
        return (
          <button
            key={starValue}
            type="button"
            disabled={disabled}
            role="radio"
            aria-checked={value === starValue}
            aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
            onClick={() => onChange(starValue)}
            className={cn(
              'rounded p-0.5 transition-colors focus-ring disabled:opacity-50',
              active ? 'text-gold-500' : 'text-zinc-300 hover:text-gold-400'
            )}
          >
            <Star
              className={cn('h-7 w-7', active ? 'fill-current' : 'fill-transparent')}
            />
          </button>
        )
      })}
    </div>
  )
}
